import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { logError } from "@/lib/logger";

export function useArchive() {
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState("All");
  const [search, setSearch] = useState("");
  const [view, setView] = useState("trending");
  const [query, setQuery] = useState("");
  const [discovering, setDiscovering] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { sort: "score" };
      if (view === "trending") params.period = "week";
      if (view === "read") params.sort = "views";
      if (view === "divisive") params.sort = "divisive";
      if (view === "discussed") params.sort = "discussed";
      if (category !== "All") params.category = category;
      if (search.trim()) params.search = search.trim();
      const { data } = await api.get("/topics", { params });
      setTopics(data);
    } catch (err) {
      logError("Failed to load archive:", err);
      toast.error("Failed to load the archive");
    } finally {
      setLoading(false);
    }
  }, [category, search, view]);

  useEffect(() => {
    load();
  }, [load]);

  const discover = useCallback(async () => {
    setDiscovering(true);
    try {
      const { data } = await api.post("/discover", { query: query.trim() || null, count: 6 });
      const jobId = data.job_id;
      const started = Date.now();
      const poll = async () => {
        if (Date.now() - started > 120000) {
          toast.error("Investigation timed out. Try again.");
          setDiscovering(false);
          return;
        }
        try {
          const { data: st } = await api.get(`/discover/status/${jobId}`);
          if (st.status === "done") {
            if (st.count > 0) {
              toast.success(`${st.count} new controversies surfaced`);
              setQuery("");
              await load();
            } else {
              toast.info("No new topics — try a different angle.");
            }
            setDiscovering(false);
          } else if (st.status === "error") {
            toast.error("Investigation failed. Try again.");
            setDiscovering(false);
          } else {
            setTimeout(poll, 2500);
          }
        } catch (err) {
          logError("Polling discover status failed, retrying:", err);
          setTimeout(poll, 2500);
        }
      };
      setTimeout(poll, 2000);
    } catch (err) {
      logError("Discovery request failed:", err);
      const msg = err.response?.status === 429
        ? (err.response?.data?.detail || "Investigation limit reached. Please try again shortly.")
        : "Investigation failed. Try again.";
      toast.error(msg);
      setDiscovering(false);
    }
  }, [query, load]);

  return {
    topics, loading, category, setCategory, search, setSearch,
    view, setView, query, setQuery, discovering, discover,
  };
}
